import {Component, OnInit} from "@angular/core";
import {EditHospitalService, NurseSettings} from "./edit-hospital.service";
import {ReportLanguageSettings} from "../report-language-settings";
import {
  ALL_EXTERNAL_ANALYSIS_TYPES,
  AllowedHospitalMeasurementTypes,
  EcgWorkflow,
  EditedHospital,
  ExternalAnalysisType,
  HospitalDetails,
  OperatorHospital
} from "./hospital.details";
import {ActivatedRoute} from "@angular/router";
import {Notifications, NotificationType} from "../../../tools/notifications/notifications";
import {localizationKey} from "../../../i18n/i18n-model";
import {HospitalIdProvider} from "./hospital-id-provider";
import {getMeasurementTypeLocalizationKey} from "../../measurement-models";
import {FormConfigBase} from "../../../tools/form/form-config-base";
import {HospitalInformation} from "./hospital-information";
import {PropertyChangeListener} from "../../../tools/form/form-field-config";
import {HospitalInformationValidator} from "./hospital-information.validator";
import {ReleaseToggleState} from "../../../tools/release-toggles/release-toggle-state";


@Component({
  selector: "app-edit-hospital",
  templateUrl: "./edit-hospital.component.html",
  standalone: false
})
export class EditHospitalComponent implements OnInit, PropertyChangeListener<HospitalInformation> {
  hospitalContext?: EditedHospital;
  hospitalDetails?: HospitalDetails;
  hospitalChainSet = false;
  eligibleOperators: (OperatorHospital | undefined)[] = [];
  selectedEligibleOperator?: number;

  protected readonly localizationKey = localizationKey;

  protected readonly getMeasurementTypeLocalizationKey = getMeasurementTypeLocalizationKey;

  private hospitalId?: number;

  hospitalDetailsFormConfig?: HospitalDetailsFormConfig;

  private hospitalInformationValidator?: HospitalInformationValidator;

  readonly allExternalAnalysisTypes = ALL_EXTERNAL_ANALYSIS_TYPES;
  readonly externalAnalysisTypeLabels: Record<ExternalAnalysisType, string> = {
    CARDIOLOGS: "Cardiologs",
    CAS: "CAS",
  };

  constructor(private readonly editHospitalService: EditHospitalService,
              private readonly route: ActivatedRoute,
              private readonly hospitalIdProvider: HospitalIdProvider,
              private readonly notifications: Notifications
  ) {
  }

  ngOnInit(): void {
    this.hospitalId = this.hospitalIdProvider.getId(this.route);

    if (this.hospitalId === undefined) {
      return;
    }

    this.getHospitalDetails();
  }

  addOperator() {
    this.editHospitalService.addOperator(this.hospitalContext!.id, this.selectedEligibleOperator!)
      .subscribe(() => this.getHospitalDetails(),
        () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("editHospitalFailedToAddOperator")));
  }

  removeOperator(operatorHospital: OperatorHospital) {
    this.editHospitalService.removeOperator(this.hospitalContext!.id, operatorHospital.id)
      .subscribe(() => this.getHospitalDetails(),
        // () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("editHospitalFailedToAddOperator"))
      );
  }

  private getHospitalDetails() {
    this.selectedEligibleOperator = undefined;
    this.editHospitalService.getHospital(this.hospitalId!)
      .subscribe((hospitalContext) => {
        this.hospitalContext = hospitalContext;
        this.hospitalDetails = hospitalContext.hospitalDetails;
        this.hospitalInformationValidator = new HospitalInformationValidator(this.hospitalDetails.hospitalInformation);
        this.hospitalDetailsFormConfig = new HospitalDetailsFormConfig(
          this.hospitalDetails.hospitalInformation,
          this,
          (property) => this.hospitalInformationValidator!.isPropertyValid(property)
        );
        this.eligibleOperators = [undefined, ...hospitalContext.eligibleOperators];
        this.hospitalChainSet = hospitalContext.hospitalDetails.hospitalChain !== undefined;
      }, () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("editHospitalFailedToLoadHospitalDetails")));
  }

  handlePropertyChange(property: keyof HospitalInformation, newValue: any): void {
    this.hospitalDetails!.hospitalInformation[property] = newValue;
    this.hospitalInformationValidator!.updatePropertyValidation(property);
  }

  handleLanguageSettingsChange(settings: ReportLanguageSettings) {
    this.hospitalDetails!.reportLanguageSettings = settings;
  }

  putHospitalInChain280Released(): boolean {
    return ReleaseToggleState
      .getInstance()
      .isReleased("PUT_HOSPITAL_IN_CHAIN_280");
  }

  handleNurseSettingsChange(settings: NurseSettings) {
    this.hospitalDetails!.nurse2Fa = settings.nurse2Fa;
    this.hospitalDetails!.nurseReportDeliveryNotification = settings.reportDeliveryNotification;
    this.hospitalDetails!.nurseInitiatesWithDeviceSerial = settings.initiateWithDeviceSerial;
  }

  handleAllowedTypesChange(types: AllowedHospitalMeasurementTypes) {
    this.hospitalDetails!.allowedMeasurementTypes = types;
  }

  handleTechniciansSettingsChange(settings: { assignSleepTechniciansEnabled: boolean, ecgWorkflow: EcgWorkflow }) {
    this.hospitalDetails!.assignSleepTechniciansEnabled = settings.assignSleepTechniciansEnabled;
    this.hospitalDetails!.ecgWorkflow = settings.ecgWorkflow;
  }

  handleChainChange(chainId: number) {
    this.hospitalDetails!.hospitalChain = this.hospitalContext!.chains.find(c => c.id === chainId);
  }

  get hospitalInformationValid(): boolean {
    return this.hospitalInformationValidator!.isFormValid() ?? true;
  }

  save() {
    this.editHospitalService.saveDetails(this.hospitalId!, this.hospitalDetails!).subscribe({
      next: () => {
        this.hospitalChainSet = this.hospitalDetails!.hospitalChain !== undefined;
        this.notifications.addNotification(NotificationType.OK, localizationKey("editHospitalSaveSuccess"));
      },
      error: () => {
        this.notifications.addNotification(NotificationType.ERROR, localizationKey("editHospitalFailedToSaveDetails"));
      }
    });
  }
}

class HospitalDetailsFormConfig extends FormConfigBase<HospitalInformation> {
  constructor(
    model: HospitalInformation,
    propertyChangeListener?: PropertyChangeListener<HospitalInformation>,
    validationProvider?: (property: keyof HospitalInformation) => boolean | undefined,
  ) {
    super(model, propertyChangeListener, validationProvider);
  }

  readonly publicName = this.createField({
    property: "publicName",
    type: "text",
    label: "hospitalInformationPublicName",
    invalidLabel: "hospitalInformationPublicNameError"
  });

  readonly operatorAppointmentDescription = this.createField({
    property: "operatorAppointmentDescription",
    type: "text",
    label: "hospitalInformationOperatorAppointmentDescription",
    invalidLabel: "hospitalInformationOperatorAppointmentDescriptionError"
  });

  readonly address = this.createField({
    property: "address",
    type: "text",
    label: "hospitalInformationAddress",
    invalidLabel: "hospitalInformationAddressError"
  });

  readonly postalCode = this.createField({
    property: "postalCode",
    type: "text",
    label: "hospitalInformationPostalCode",
    invalidLabel: "hospitalInformationPostalCodeError"
  });

  readonly city = this.createField({
    property: "city",
    type: "text",
    label: "hospitalInformationCity",
    invalidLabel: "hospitalInformationCityError"
  });

}


